import React, { useState } from "react";
import {
    Box,
    Button,
    Divider,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { StatusChip } from "./StatusChip";
import { useCapabilities } from "../../hooks/useCapabilities";
import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib";
import { getMe } from "../../features/auth";

interface ApprovalsProps {
    status: "Pending" | "On Hold" | "Approved";
    color: "green" | "red" | "amber";
    name: string;
    userId: number;
    role: string;
    note: string;
    date: string;
    onSave: (status: "on_hold" | "approved" | "pending", notes: null | string) => void;
    isLoading?: boolean;
    isDiabledGateDecision?: boolean;
    isStageGateCompleted: boolean
}

export const Approvals: React.FC<ApprovalsProps> = ({
    status,
    color,
    name,
    userId,
    role,
    note,
    date,
    onSave,
    isLoading = false,
    isDiabledGateDecision = false,
    isStageGateCompleted
}) => {

    const [notes, setNotes] = useState<string | null>(null);

    const { isAdminPerson } = useCapabilities();

    const { data } = useQuery({
      queryKey: qk.me(),
      queryFn: getMe,
    });

    const user = data?.user?.id;

    const canApprove = isAdminPerson || (user !== undefined && Number(user) === userId);

    return (
        <Box
            borderColor={"divider"}
            borderRadius={1}
            px={2}
            py={2}
            sx={{ borderWidth: "1px", borderStyle: "solid" }}
            mb={2}
            display={"flex"}
            alignItems={"start"}
            justifyContent={"start"}
            flexDirection={"column"}
            gap={2}
        >
            <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
            >
                <Box display={"flex"} alignItems={"start"} justifyContent={"start"} flexDirection={"column"}>
                    <Typography variant="body2" color="text.primary">
                        {name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {role}
                    </Typography>
                </Box>
                <StatusChip label={status} color={color || "amber"} />
            </Box>
            {status === "Pending" ?  ( canApprove &&
                <Box display={"flex"} justifyContent={"end"} flexDirection={"column"} width={"100%"} gap={1}>
                    <TextField
                        fullWidth
                        label="Note"
                        value={notes ?? ""}
                        type="note"
                        size="small"
                        onChange={(event) => setNotes(event.target.value)}
                        placeholder="Enter your notes"
                        required
                        disabled={isLoading}
                    />
                    <Divider sx={{ my: 1 }} />
                    <Box display={"flex"} justifyContent={"end"} gap={1}>
                        <Button
                            variant="text"
                            startIcon={!isLoading ? <Close /> : <CircularProgress size={14} />}
                            sx={{ color: "error.main" }}
                            size="small"
                            onClick={() => onSave("on_hold", notes)}
                            disabled={!canApprove || isLoading || isDiabledGateDecision}
                        >
                            on hold
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            startIcon={!isLoading ? <CheckCircleOutlineIcon /> : <CircularProgress size={14} />}
                            sx={{ color: "success.main" }}
                            onClick={() => onSave("approved", notes)}
                            disabled={!canApprove || isLoading || isDiabledGateDecision}
                        >
                            approve
                        </Button>
                    </Box>
                </Box>
            ) : ( canApprove &&
                <Box display={"flex"} justifyContent={"end"} flexDirection={"column"} width={"100%"} gap={1}>
                    <Box
                        display={"flex"}
                        alignItems={"start"}
                        justifyContent={"start"}
                        flexDirection={"column"}
                        bgcolor={"action.hover"}
                        width={"100%"}
                        p={2}
                        borderRadius={1}
                    >
                        <Typography variant="caption" color="text.primary">
                            {status} by {name} on {date}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {note}
                        </Typography>
                    </Box>
                    {
                        !isStageGateCompleted && (
                            <Box display={"flex"} justifyContent={"end"} gap={1}>
                                <Button
                                    variant="text"
                                    startIcon={!isLoading ? <HourglassTopIcon /> : <CircularProgress size={14} />}
                                    sx={{ color: "text.main" }}
                                    size="small"
                                    onClick={() => onSave("pending", "Update as pending")}
                                    disabled={isLoading || isDiabledGateDecision}
                                >
                                    Pending
                                </Button>
                            </Box>
                        )
                    }
                </Box>
            )}
        </Box>
    );
};
